import React, { useEffect } from 'react';
import { PortalProvider } from 'react-portal-hook';
import { MutableSnapshot, RecoilRoot } from 'recoil';
import { Offer } from '~/common/models';
import { EnquiryForm } from '~/common/components/offer-enquiry-form';
import { ActiveSpecialOffer } from '../special-offer/store';
import { OffersLocation } from '../special-offers/components/offer-header';
import { OfferBanner } from '../special-offer/components/offer-banner';
import { OfferContentV2 } from './components/offer-content-v2';
import { OfferEnquirySection } from './components/offer-enquiry-section';
import { OfferDisclaimers } from './components/offer-disclaimers';
import { PrivacyStatement } from './components/privacy-statement';
import { ActiveOfferEnquiryForm, RecaptchaSiteKey, ThankYouUrl } from './store';
import styles from './index.module.scss';

interface SpecialOfferV2Props {
  /**
   * Null when the offers API could not serve the offer. The view model's SpecialOffer is nullable and the
   * controller passes it through whatever happened, so the type says what can actually arrive.
   */
  offer: Offer | null;
  /**
   * The form resolved for this offer, or null when the offer has no form and no default exists.
   *
   * Resolved by the controller during the page request and passed in as a prop, so the browser makes no
   * second call for it. (Pages under src/pages are not in the SSR bundle - only src/macros is - so this
   * component renders in the browser, as the existing offer page does.)
   */
  enquiryForm?: EnquiryForm | null;
  /**
   * reCAPTCHA site key for the enquiry form, from the same configuration the older enquiry page reads. The
   * key is public by design - the secret half stays on the server, which is what verifies the token.
   */
  recaptchaSiteKey?: string;
  /**
   * Where to send the customer once the enquiry is away, from the server's own route table. The confirmation
   * is a page of its own now, so this page never renders one.
   */
  thankYouUrl?: string;
}

/**
 * The redesigned special offer page.
 *
 * Lives beside the existing `SpecialOffer` rather than replacing it, the same way `showroomv3` sits beside
 * `showroom`: both read the same offer data, so a page can be switched over without a content migration and
 * switched back if the redesign needs more work.
 *
 * The banner and the suburb picker are the existing components - the redesign does not change them. What is
 * new is the single centred copy column and the enquiry form on the page instead of behind a link.
 */
export const SpecialOfferV2 = (props: SpecialOfferV2Props): JSX.Element => {
  const initialize = ({ set }: MutableSnapshot) => {
    set(ActiveSpecialOffer, props.offer);
    set(ActiveOfferEnquiryForm, props.enquiryForm ?? null);
    set(RecaptchaSiteKey, props.recaptchaSiteKey ?? null);
    set(ThankYouUrl, props.thankYouUrl ?? null);
  };

  useEffect(() => {
    window['pushDigitalData']({
      event: '_pageLoaded',
    });
  }, []);

  // The offer is null when the offers API could not serve it - a wrong slug, an expired offer, or the API
  // being down. Saying so beats the alternative the components alone would give: a location picker above an
  // empty carousel, with nothing to explain it.
  if (!props.offer) {
    return (
      <div className={styles.offerNotFound} data-testid="offer-not-found">
        Offer not found
      </div>
    );
  }

  return (
    <RecoilRoot initializeState={initialize}>
      <PortalProvider>
        <div className={styles.offerLocation}>
          <OffersLocation />
        </div>
        <OfferBanner />
        <OfferContentV2 />
        <OfferEnquirySection />
        {/* The collection notice belongs to the form above it; the offer's own small print comes last. */}
        <PrivacyStatement />
        <OfferDisclaimers />
      </PortalProvider>
    </RecoilRoot>
  );
};
